import { CornerLeftUp, EyeOff, Layers, Lock, Rows3 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  SECTION_LOCKED_DISPLAYS,
  SPACE_LOCKED_DISPLAYS,
  type SectionLockedDisplay,
  type SpaceLockedDisplay
} from '@/types/access'

const ICONS: Record<SectionLockedDisplay | SpaceLockedDisplay, typeof Lock> = {
  hide: EyeOff,
  section: Rows3,
  section_and_spaces: Layers,
  inherit: CornerLeftUp,
  show: Lock
}

type LockedDisplayPickerProps =
  | { kind: 'section'; value: SectionLockedDisplay; onChange: (value: SectionLockedDisplay) => void }
  | { kind: 'space'; value: SpaceLockedDisplay; onChange: (value: SpaceLockedDisplay) => void }

/**
 * "Para quem não tem acesso" (F-161a): whether whoever the section or space
 * keeps out sees it with a padlock (and the way in) or does not see it at all.
 *
 * Same pills as the visibility right above it. An area only the team can open
 * is never shown to anyone else, whatever is picked, so the options are drawn
 * disabled with the reason instead of offering a choice that does nothing.
 */
export function LockedDisplayPicker(props: LockedDisplayPickerProps & {
  disabled?: boolean
  /** Only the team can open it (private with nobody in "Who has access"). */
  adminOnly?: boolean
  className?: string
}) {
  const { t } = useTranslation(['common'])
  const { kind, disabled, adminOnly = false, className } = props
  const options: string[] = kind === 'section' ? SECTION_LOCKED_DISPLAYS : SPACE_LOCKED_DISPLAYS
  const isDisabled = disabled || adminOnly

  return (
    <div className={cn('space-y-2', className)} data-locked-display-picker={kind}>
      <label className="text-sm font-medium">{t('common:access.lockedDisplay.title')}</label>

      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('common:access.lockedDisplay.title')}>
        {options.map((option) => {
          const Icon = ICONS[option as SectionLockedDisplay | SpaceLockedDisplay]
          const checked = props.value === option
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={checked}
              disabled={isDisabled}
              title={t(`common:access.lockedDisplay.${kind}Hints.${option}`)}
              onClick={() => (props.onChange as (value: string) => void)(option)}
              className={cn(
                'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                checked && !adminOnly ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 hover:bg-gray-200',
                isDisabled && 'cursor-not-allowed opacity-50'
              )}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span>{t(`common:access.lockedDisplay.${kind}.${option}`)}</span>
            </button>
          )
        })}
      </div>

      {adminOnly
        ? <p className="text-xs text-amber-700">{t('common:access.lockedDisplay.adminOnlyDisabled')}</p>
        : <p className="text-xs text-gray-500">{t(`common:access.lockedDisplay.${kind}Hints.${props.value}`)}</p>}
    </div>
  )
}
